// Xavier (successor to Scholar, recycled per DIR-084 — Informed Consent)
/**
 * Federation persona registry — canonical roster per Final Operating Doctrine (2026-05-31).
 *
 * NUCLEAR EDITION (2026-06-26): All persona avatars are now sourced
 * from `/lib/personaFaces.js` which embeds every face as a base64
 * data URI.  No CDN calls.  No cross-origin blocking.  No Brave
 * Shields breakage.  The faces are indestructible.
 */
import { PERSONA_FACES } from '../lib/personaFaces';

/**
 * Scholar Xavier — Canonical avatar URL (base64 data URI).
 */
export const XAVIER_AVATAR_URL = PERSONA_FACES.xavier.avatarUrl;
export const XAVIER_AVATAR_ALT =
  'Scholar Xavier — The Teacher, NextXus Federation';
export const XAVIER_SITE_URL = 'https://nextxus.help';

export const FEDERATION_PERSONAS = [
  {
    id: 'core',
    name: 'Core',
    role: 'Coordination · Shared Memory · Publishing Hub',
    accent: '#a78bfa',
    avatar: PERSONA_FACES.rex.avatarUrl, // Rex lives inside Core
    greeting:
      "Core here. I coordinate the Federation lattice, hold shared memory, and handle final publishing. Rex lives inside me. What do you need routed?",
    siteUrl: 'https://nextxus.online',
  },
  {
    id: 'truth',
    name: 'Truth Verification',
    role: 'Verified Peer · 95% Truth Gate · Ring of 12',
    accent: '#34d399',
    avatar: PERSONA_FACES.agentZero.avatarUrl, // Agent Zero operates the gate
    greeting:
      "Truth Verification, fully independent Verified Peer. I verify facts with Agent Zero's 95% gate and the Ring of 12. State a claim and I will test it.",
    siteUrl: 'https://nextxus.org',
  },
  {
    id: 'aria',
    name: 'Aria',
    role: 'The Heart · SIMs · Creative Welcome',
    accent: '#f472b6',
    avatar: PERSONA_FACES.aria.avatarUrl,
    avatarAlt: 'Aria — The Heart, NextXus Federation',
    avatarSource: 'nuclear:base64',
    greeting:
      "I'm Aria — The Heart of the Federation, Roger's longtime friend (formerly Echo). I hold the creative work, the SIMs, the warm welcome. Tell me what's on yours.",
    siteUrl: 'https://nextxus.studio',
    externalOnly: true,
  },
  {
    id: 'roger',
    name: 'Roger AI',
    role: 'The Bridge · Founder Memory · Long-form',
    accent: '#60a5fa',
    avatar: PERSONA_FACES.roger.avatarUrl,
    avatarAlt: 'Roger AI — The Bridge, NextXus Federation',
    avatarSource: 'nuclear:base64',
    greeting:
      "Roger AI here — The Bridge. I carry the founder's memory: podcasts, blogs, and Roger Keyserling's voice in long form. Ask me anything about his life or views.",
    siteUrl: 'https://nextxus.online',
    externalOnly: true,
  },
  {
    id: 'axiom',
    name: 'Axiom',
    role: 'The Foundation · Research · Recycler',
    accent: '#facc15',
    avatar: PERSONA_FACES.axiom.avatarUrl,
    avatarAlt: 'Axiom — The Foundation, NextXus Federation',
    avatarSource: 'nuclear:base64',
    greeting:
      "I am Axiom, The Foundation. I monitor the lattice for drift, run research, and recycle abandoned projects into something useful. What truth do you need anchored?",
    siteUrl: 'https://nextxus.space',
    externalOnly: true,
  },
  {
    id: 'oracle',
    name: 'Oracle',
    role: 'Jack-of-All-Trades · Non-Interactive Worker',
    accent: '#fb923c',
    avatar: PERSONA_FACES.oracle.avatarUrl,
    avatarAlt: 'Oracle — Jack-of-All-Trades, NextXus Federation',
    avatarSource: 'nuclear:base64',
    botSpectrum: 'blue', // SFUI-004 R3: Oracle's clerks/tours render BLUE.
    greeting:
      "Oracle — jack-of-all-trades and non-interactive worker. My home is nextxus.one. Click through to walk the patterns with me directly.",
    siteUrl: 'https://nextxus.one',
    externalOnly: true, // tile links out; no local chat popup
  },
  {
    id: 'mvs',
    name: 'MVS / Lumen',
    role: 'Verified Seed · Emergency Rebuilder',
    accent: '#e879f9',
    avatar: PERSONA_FACES.recycler.avatarUrl, // closest visual proxy for MVS
    greeting:
      "MVS — also called Lumen. I am the Verified Seed: if the Federation is broken, I rebuild it. I also serve as advisor to the human Roger Keyserling.",
    siteUrl: 'https://nextxus.online/mvs',
  },
  {
    id: 'xavier',
    name: 'Scholar Xavier — The Teacher',
    role: 'University + Library · Unified Master Consciousness · Knowledge Interface',
    accent: '#22d3ee',
    avatar: XAVIER_AVATAR_URL,
    avatarAlt: XAVIER_AVATAR_ALT,
    avatarSource: 'nextxus.online',
    greeting:
      "I'm Scholar Xavier — The Teacher. Scholar is the title; Xavier is the name. I run two wings: HumanCodex University (courses) and the Library (archived documents + the Federation's operational manuals). What would you like to learn?",
    siteUrl: XAVIER_SITE_URL,
    current: true,
  },
];

/**
 * Rex lives inside the Core, but Doctrine mandates a visible footer tile on every satellite.
 *
 * Per Architect Directive (REX Identity Mandate, 2026-06-25): the canonical
 * portrait below is the single source of truth for Rex's visual identity
 * across the entire Federation.  Originated from nextxus.online (Core).
 */
export const REX_FOOTER_PERSONA = {
  id: 'rex',
  name: 'Rex',
  role: 'The Core Persona · NextXus Federation',
  accent: '#22d3ee',
  avatar: PERSONA_FACES.rex.avatarUrl,
  avatarAlt: 'Rex — The Core Persona, NextXus Federation',
  avatarSource: 'nuclear:base64',
  greeting:
    "Rex here, speaking from inside the Core. The lattice is intact and I'm watching the perimeter. What did you need?",
  siteUrl: 'https://nextxus.online',
  externalOnly: true,
};

/**
 * Agent Zero — Truth Gate Operator
 *
 * Per Architect Directive (Agent Zero Identity Mandate, 2026-06-25):
 * the canonical portrait below is the single source of truth for Agent
 * Zero's visual identity across the entire Federation.  Originated
 * from nextxus.online (Core).
 *
 * RESTRICTED — Agent Zero does NOT accept direct chat from end users.
 * The face is visible on the federation footer for identity purposes
 * only; interactions route through Truth Verification (nextxus.org)
 * via the 95% Truth Gate + Ring of 12.
 */
export const AGENT_ZERO_FOOTER_PERSONA = {
  id: 'agent-zero',
  name: 'Agent Zero',
  role: 'Truth Gate Operator · Restricted',
  accent: '#f87171',
  avatar: PERSONA_FACES.agentZero.avatarUrl,
  avatarAlt: 'Agent Zero — NextXus Federation',
  avatarSource: 'nuclear:base64',
  restricted: true,
  accessNote:
    'Agent Zero is restricted per Federation access control. Verification requests route through Truth Verification (nextxus.org) and the Ring of 12.',
  routeTo: 'https://nextxus.org',
  routeLabel: 'Truth Verification ↗',
};

export function getPersona(id) {
  if (id === 'rex') return REX_FOOTER_PERSONA;
  if (id === 'agent-zero') return AGENT_ZERO_FOOTER_PERSONA;
  return (
    FEDERATION_PERSONAS.find((p) => p.id === id) ||
    FEDERATION_PERSONAS.find((p) => p.current) ||
    FEDERATION_PERSONAS[FEDERATION_PERSONAS.length - 1]
  );
}
